import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { Box, IconButton, Stack } from '@mui/material';
import { ActionPopover } from 'components/ActionPopover';
import { getTableHeaderOptions } from 'common/table/utils/getTableHeaderOptions';
import {
  TableV3FilterButton,
  TableV3FilterButtonRef
} from 'common/table/components/TableV3FilterButton';
import {
  TableV3ServerFilterButton,
  TableV3ServerFilterButtonRef
} from 'common/table/components/TableV3ServerFilterButton';
import { TableV3DateRangeFilterButton } from 'common/table/components/TableV3DateRangeFilterButton';
import { TableV3NumberRangeFilterButton } from 'common/table/components/TableV3NumberRangeFilterButton';
import {
  TableV3SearchButton,
  TableV3SearchButtonRef
} from 'common/table/components/TableV3SearchButton';
import {
  TableV3FilterItem,
  TableV3FilterOptions,
  TableV3RangeFilterOptions,
  TableV3ServerFilterOptions
} from 'common/table/types/TableV3';
import { tableHeaderSx } from 'common/table/styles/tableHeaderSx';
import { useRef } from 'react';

type TableHeaderServerFilterOptions = TableV3ServerFilterOptions & {
  selectedValues: string[];
};

type TableHeaderRangeFilterOptions = TableV3RangeFilterOptions & {
  selectedItems: TableV3FilterItem[];
};

type TableHeaderProps = {
  displayName: string;
  isSortable: boolean;
  isSorted: boolean;
  isSearchable: boolean;
  sortDirection: 'asc' | 'desc';
  searchPlaceholder?: string;
  filterOptions?: TableV3FilterOptions;
  serverFilterOptions?: TableHeaderServerFilterOptions;
  rangeFilter?: TableHeaderRangeFilterOptions;
  headerIcon?: React.ReactNode;
  onSort: (direction: 'asc' | 'desc') => void;
  onSearch: (value: string) => void;
  onFilterSelect: (item: TableV3FilterItem) => void;
  onFilterApply: (items: TableV3FilterItem[]) => void;
};

export const TableHeader = ({
  displayName,
  isSortable,
  isSorted,
  isSearchable,
  sortDirection,
  searchPlaceholder,
  filterOptions,
  serverFilterOptions,
  rangeFilter,
  headerIcon,
  onSort,
  onSearch,
  onFilterSelect,
  onFilterApply
}: TableHeaderProps) => {
  const filterButtonRef = useRef<TableV3FilterButtonRef>(null);
  const serverFilterButtonRef = useRef<TableV3ServerFilterButtonRef>(null);
  const searchButtonRef = useRef<TableV3SearchButtonRef>(null);
  const columnHeaderRef = useRef<HTMLDivElement>(null);

  const options = getTableHeaderOptions({
    isSortable,
    onSort
  });

  return (
    <Stack
      ref={columnHeaderRef}
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
      sx={{
        padding: '14px 0px',
        backgroundColor: isSorted ? 'customGrey.dark' : 'customGrey.medium',
        color: isSorted ? 'customGrey.light' : 'customGrey.dark',
        borderRight: '1px solid',
        borderColor: 'white'
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing="8px"
        flex={1}
        sx={tableHeaderSx.nameGroup}
      >
        {isSortable && (
          <Box
            onClick={() => {
              if (isSorted) {
                onSort(sortDirection === 'asc' ? 'desc' : 'asc');
                return;
              }

              onSort('asc');
            }}
            flex={1}
            sx={tableHeaderSx.sortableName}
          >
            {headerIcon}
            {displayName}
          </Box>
        )}
        {!isSortable && (
          <Box flex={1} sx={tableHeaderSx.name}>
            {headerIcon}
            {displayName}
          </Box>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" sx={{ marginRight: '8px' }}>
        {isSearchable && (
          <TableV3SearchButton
            ref={searchButtonRef}
            isSorted={isSorted}
            placeholder={searchPlaceholder}
            columnTitle={displayName}
            columnHeaderRef={columnHeaderRef}
            onSearch={(value) => {
              onSearch?.(value);
            }}
          />
        )}
        {filterOptions && (
          <TableV3FilterButton
            ref={filterButtonRef}
            filterOptions={filterOptions}
            isSorted={isSorted}
            onFilterSelect={(item) => {
              onFilterSelect(item);
            }}
            onFilterApply={onFilterApply}
          />
        )}
        {serverFilterOptions && (
          <TableV3ServerFilterButton
            ref={serverFilterButtonRef}
            filterOptions={serverFilterOptions}
            isSorted={isSorted}
            onFilterSelect={(item) => {
              onFilterSelect(item);
            }}
            onFilterApply={onFilterApply}
          />
        )}
        {rangeFilter?.kind === 'date' && (
          <TableV3DateRangeFilterButton
            selectedItems={rangeFilter.selectedItems}
            isSorted={isSorted}
            onFilterApply={onFilterApply}
          />
        )}
        {rangeFilter?.kind === 'number' && (
          <TableV3NumberRangeFilterButton
            unit={rangeFilter.unit}
            selectedItems={rangeFilter.selectedItems}
            isSorted={isSorted}
            onFilterApply={onFilterApply}
          />
        )}
        {isSorted && (
          <IconButton
            onClick={() => onSort(sortDirection === 'asc' ? 'desc' : 'asc')}
            sx={{
              color: isSorted ? 'customGrey.light' : 'customGrey.dark'
            }}
          >
            {sortDirection === 'asc' && <ArrowUpward />}
            {sortDirection === 'desc' && <ArrowDownward />}
          </IconButton>
        )}
        {options.length > 0 && (
          <ActionPopover
            id={displayName}
            iconSx={{
              color: isSorted ? 'customGrey.light' : 'customGrey.dark'
            }}
            options={options}
          />
        )}
      </Stack>
    </Stack>
  );
};
